import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Search, History, Archive, RotateCcw, Trash2, Upload } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Patients = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: 'Male',
    phone: '',
    address: '',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients?include_archived=${showArchived}`);
      setPatients(response.data);
      setFilteredPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    const filtered = patients.filter(
      (patient) =>
        (patient.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patient.phone || '').includes(searchTerm)
    );
    setFilteredPatients(filtered);
  }, [searchTerm, patients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      phone: formData.phone.trim(),
      name: formData.name.trim(),
      gender: formData.gender || 'Other',
      age: formData.age === '' || formData.age === null ? null : parseInt(formData.age, 10),
      address: formData.address.trim(),
    };
    try {
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.id}`, payload);
      } else {
        await axios.post(`${API}/patients`, payload);
      }
      fetchPatients();
      resetForm();
    } catch (error) {
      console.error('Error saving patient:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : (detail || 'Failed to save patient');
      alert(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/patients/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchPatients();
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert(error.response?.data?.detail || 'Failed to delete patient');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (patient) => {
    try {
      await axios.post(`${API}/patients/${patient.id}/restore`);
      fetchPatients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to restore patient');
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    setHardDeleteLoading(true);
    try {
      await axios.delete(`${API}/patients/${hardDeleteTarget.id}?hard=true`);
      setHardDeleteTarget(null);
      fetchPatients();
    } catch (error) {
      console.error('Error permanently deleting patient:', error);
      alert(error.response?.data?.detail || 'Failed to permanently delete patient');
    } finally {
      setHardDeleteLoading(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', importFile);
      const { data } = await axios.post(`${API}/admin/import-contacts`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      setImportFile(null);
      fetchPatients();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImportLoading(false);
    }
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name || '',
      age: patient.age ?? '',
      gender: patient.gender || 'Male',
      phone: patient.phone || '',
      address: patient.address || '',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', age: '', gender: 'Male', phone: '', address: '' });
    setEditingPatient(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="patients-title">
            Patients
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage patient records</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              className="secondary-btn flex items-center gap-2"
              onClick={() => { setShowImport(true); setImportResult(null); }}
              data-testid="import-contacts-btn"
              title="Bulk import patients from Excel (.xlsx)"
            >
              <Upload size={18} />
              Import Contacts
            </button>
          )}
          <button
            className="primary-btn flex items-center gap-2"
            onClick={() => setShowForm(true)}
            data-testid="add-patient-btn"
          >
            <Plus size={20} />
            Add Patient
          </button>
        </div>
      </div>

      {showImport && (
        <div className="card p-6 mb-8" data-testid="import-contacts-modal">
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Bulk Import Contacts
          </h2>
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            Upload an <strong>.xlsx</strong> with columns: <code>Name | Phone number</code>. Rows with phone digits &lt; 10 are skipped. Duplicates (within the file and against existing patients) are automatically skipped.
          </p>
          <form onSubmit={handleImport}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              required
              data-testid="import-file-input"
              className="mb-4"
            />
            <div className="flex gap-3">
              <button
                type="submit"
                className="primary-btn"
                disabled={!importFile || importLoading}
                data-testid="import-submit-btn"
              >
                {importLoading ? 'Importing...' : 'Import'}
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => { setShowImport(false); setImportFile(null); setImportResult(null); }}
                data-testid="import-cancel-btn"
              >
                Close
              </button>
            </div>
          </form>
          {importResult && (
            <div className="mt-6 p-4 rounded" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }} data-testid="import-result">
              <p className="font-bold mb-1">Import complete</p>
              <ul className="text-sm space-y-0.5">
                <li>Rows processed: <strong>{importResult.total_rows_processed}</strong></li>
                <li>Inserted: <strong>{importResult.inserted}</strong></li>
                <li>Skipped (short/invalid phone): {importResult.skipped_short_phone}</li>
                <li>Skipped (duplicate within file): {importResult.skipped_duplicate_in_file}</li>
                <li>Skipped (already in DB): {importResult.skipped_already_exists}</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="card p-6 mb-8" data-testid="patient-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingPatient ? 'Edit Patient' : 'Add New Patient'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Phone <span style={{ color: '#EF4444' }}>*</span></label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="patient-phone-input"
                />
              </div>
              <div>
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="patient-name-input"
                />
              </div>
              <div>
                <label>Age</label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  data-testid="patient-age-input"
                />
              </div>
              <div>
                <label>Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  data-testid="patient-gender-select"
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                  data-testid="patient-address-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="patient-submit-btn">
                {editingPatient ? 'Update' : 'Save'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="patient-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="mb-4 flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={20} style={{ color: '#6B7280' }} />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="patient-search-input"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm" data-testid="show-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-auto"
            />
            <Archive size={16} style={{ color: '#92400E' }} />
            <span style={{ color: '#2D3A2A' }}>Show archived</span>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="patients-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((patient) => (
                <tr key={patient.id} data-testid={`patient-row-${patient.id}`} style={patient.archived ? { backgroundColor: '#FEF3C7', opacity: 0.85 } : {}}>
                  <td className="font-medium" style={{ color: '#2D3A2A' }}>
                    <div className="flex items-center gap-2">
                      {patient.name || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>—</span>}
                      {patient.archived && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDE68A', color: '#92400E' }} data-testid={`archived-badge-${patient.id}`}>
                          ARCHIVED
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{patient.age || '—'}</td>
                  <td>{patient.gender || '—'}</td>
                  <td>{patient.phone}</td>
                  <td>{patient.address || '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <Link
                        to={`/patients/${patient.id}/history`}
                        className="p-2 rounded hover:bg-gray-100 transition-colors"
                        title="View History"
                        data-testid={`view-history-${patient.id}`}
                      >
                        <History size={18} style={{ color: '#1E40AF' }} />
                      </Link>
                      {patient.archived ? (
                        <>
                          <button
                            onClick={() => handleRestore(patient)}
                            className="p-2 rounded hover:bg-gray-100 transition-colors"
                            title="Restore"
                            data-testid={`restore-patient-${patient.id}`}
                          >
                            <RotateCcw size={18} style={{ color: '#065F46' }} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => setHardDeleteTarget(patient)}
                              className="p-2 rounded hover:bg-red-50 transition-colors"
                              title="Permanently delete"
                              data-testid={`hard-delete-patient-${patient.id}`}
                            >
                              <Trash2 size={18} style={{ color: '#EF4444' }} />
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(patient)}
                            className="p-2 rounded hover:bg-gray-100 transition-colors"
                            data-testid={`edit-patient-${patient.id}`}
                          >
                            <Edit size={18} style={{ color: '#990099' }} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(patient)}
                            className="p-2 rounded hover:bg-gray-100 transition-colors"
                            title="Archive"
                            data-testid={`delete-patient-${patient.id}`}
                          >
                            <Archive size={18} style={{ color: '#92400E' }} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => setHardDeleteTarget(patient)}
                              className="p-2 rounded hover:bg-red-50 transition-colors"
                              title="Permanently delete"
                              data-testid={`hard-delete-patient-${patient.id}`}
                            >
                              <Trash2 size={18} style={{ color: '#EF4444' }} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredPatients.length === 0 && (
            <div className="text-center py-8" style={{ color: '#6B7280' }} data-testid="no-patients">
              No patients found
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Archive Patient"
        message={`Are you sure you want to archive ${deleteTarget?.name || deleteTarget?.phone}? Their records will be preserved and can be restored later. Use Data Cleanup for permanent deletion.`}
        confirmLabel="Archive Patient"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        testIdPrefix="delete-patient"
      />

      <ConfirmDialog
        open={!!hardDeleteTarget}
        title="Permanently delete patient?"
        message={`This will PERMANENTLY remove ${hardDeleteTarget?.name || hardDeleteTarget?.phone} from the system. Their consultations, bills, and procedures will remain but become orphaned. This action cannot be undone.`}
        confirmLabel="Yes, permanently delete"
        loading={hardDeleteLoading}
        onConfirm={handleHardDelete}
        onCancel={() => setHardDeleteTarget(null)}
        testIdPrefix="hard-delete-patient"
      />
    </div>
  );
};

export default Patients;
